import React, { useState } from 'react';
import { FaBook, FaGraduationCap, FaChartLine, FaClock, FaUsers, FaCheckCircle, FaFileAlt, FaLightbulb, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const UPSCPage = () => {
  const [expandedStream, setExpandedStream] = useState(null);
  const [expandedSubject, setExpandedSubject] = useState(null);

  const streams = [
    {
      id: 'ias',
      title: 'Indian Administrative Service (IAS)',
      description: 'Join the elite civil service cadre and serve as a district collector, policy maker, and administrator at various levels of government.',
      icon: <FaGraduationCap />
    },
    {
      id: 'ips',
      title: 'Indian Police Service (IPS)',
      description: 'Lead law enforcement operations, maintain public order, and ensure national security as a police officer at the highest levels.',
      icon: <FaUsers />
    },
    {
      id: 'ifs',
      title: 'Indian Foreign Service (IFS)',
      description: 'Represent India on the global stage as a diplomat, working in embassies and high commissions worldwide.',
      icon: <FaChartLine />
    },
    {
      id: 'other',
      title: 'Other Central Services',
      description: 'Join various Group A and Group B services including IRS, Indian Audit & Accounts Service, and more specialized services.',
      icon: <FaBook />
    }
  ];

  const examStructure = [
    {
      stage: 'Preliminary Examination',
      papers: ['General Studies Paper I (200 marks)', 'General Studies Paper II - CSAT (200 marks)'],
      nature: 'Objective Type (Multiple Choice)',
      duration: '2 hours per paper'
    },
    {
      stage: 'Main Examination',
      papers: [
        'Essay (250 marks)',
        'General Studies I (250 marks)',
        'General Studies II (250 marks)',
        'General Studies III (250 marks)',
        'General Studies IV (250 marks)',
        'Optional Subject Paper I (250 marks)',
        'Optional Subject Paper II (250 marks)'
      ],
      nature: 'Descriptive Type',
      duration: '3 hours per paper'
    },
    {
      stage: 'Personality Test',
      papers: ['Interview'],
      nature: 'Personal Interview',
      duration: '275 marks'
    }
  ];

  const subjects = [
    {
      id: 'gs1',
      name: 'General Studies Paper I',
      category: 'Prelims & Mains',
      topics: [
        'Indian Heritage and Culture',
        'History and Geography of the World and Society',
        'Modern Indian History (from about the middle of the eighteenth century)',
        'The Freedom Struggle and its significance',
        'Post-independence consolidation and reorganization',
        'Indian Society - salient features, unity in diversity',
        'Role of women and women\'s organization',
        'Social empowerment, communalism, regionalism & secularism',
        'Urbanization, their problems and their remedies',
        'Geographical features and their location',
        'Distribution of key natural resources',
        'Important Geophysical phenomena'
      ],
      syllabus: 'Covers Indian culture, history from ancient to modern times, world geography, society, diversity, and demographics of India.'
    },
    {
      id: 'gs2',
      name: 'General Studies Paper II (CSAT)',
      category: 'Prelims',
      topics: [
        'Comprehension',
        'Interpersonal skills including communication skills',
        'Logical reasoning and analytical ability',
        'Decision making and problem solving',
        'General mental ability',
        'Basic numeracy and data interpretation'
      ],
      syllabus: 'Qualifying paper focused on aptitude, comprehension, and analytical skills. Minimum 33% marks required.'
    },
    {
      id: 'gs2mains',
      name: 'General Studies Paper II (Mains)',
      category: 'Mains',
      topics: [
        'Indian Constitution - historical underpinnings, evolution, features',
        'Functions and responsibilities of the Union and the States',
        'Federal structure, devolution of powers and finances',
        'Comparison of the Indian constitutional scheme with other countries',
        'Parliament and State Legislatures - structure, functioning, conduct of business',
        'Structure, organization and functioning of the Executive and the Judiciary',
        'Government policies and interventions for development',
        'Development processes and the development industry',
        'Role of civil services in a democracy',
        'India and its neighborhood relations',
        'Bilateral, regional and global groupings and agreements',
        'Effect of policies and politics of developed and developing countries',
        'Important International institutions, agencies and fora',
        'Welfare schemes for vulnerable sections'
      ],
      syllabus: 'Governance, Constitution, Polity, Social Justice and International relations'
    },
    {
      id: 'gs3',
      name: 'General Studies Paper III',
      category: 'Mains',
      topics: [
        'Indian Economy and issues relating to planning, mobilization of resources',
        'Inclusive growth and issues arising from it',
        'Government Budgeting',
        'Major crops cropping patterns, distribution, e-technology in agriculture',
        'Issues related to direct and indirect farm subsidies',
        'Land reforms in India',
        'Food processing and related industries',
        'Technology missions, economics of animal-rearing',
        'Infrastructure: Energy, Ports, Roads, Airports, Railways',
        'Science and Technology developments and applications',
        'Achievements of Indians in science & technology',
        'Awareness in the fields of IT, Space, Computers, robotics, nano-technology, bio-technology',
        'Issues relating to intellectual property rights',
        'Conservation, environmental pollution and degradation',
        'Disaster and disaster management',
        'Linkages between development and spread of extremism',
        'Role of external state and non-state actors in creating challenges to internal security',
        'Challenges to internal security through communication networks',
        'Money-laundering and its prevention',
        'Security challenges and their management',
        'Border Management, Organized crime including funding'
      ],
      syllabus: 'Technology, Economic Development, Bio-diversity, Environment, Security and Disaster Management'
    },
    {
      id: 'gs4',
      name: 'General Studies Paper IV (Ethics)',
      category: 'Mains',
      topics: [
        'Ethics and Human Interface',
        'Attitude: content, structure, function',
        'Moral and political attitudes',
        'Social influence and persuasion',
        'Aptitude and foundational values for Civil Service',
        'Integrity, impartiality and non-partisanship, objectivity',
        'Dedication to public service',
        'Empathy, tolerance and compassion towards the weaker-sections',
        'Emotional intelligence - concepts and applications',
        'Contributions of moral thinkers and philosophers from India and world',
        'Public/Civil service values and Ethics in Public administration',
        'Probity in Governance',
        'Case Studies on above issues'
      ],
      syllabus: 'Ethics, Integrity, and Aptitude'
    },
    {
      id: 'essay',
      name: 'Essay Paper',
      category: 'Mains',
      topics: [
        'Candidates may be required to write essays on multiple topics',
        'Topics will test critical thinking and writing skills',
        'Essays on contemporary issues, philosophical topics, social issues',
        'Abstract topics requiring creative expression',
        'Topics related to national and international significance'
      ],
      syllabus: 'Comprehensive essay writing on diverse topics to test articulation, coherence, and structured thinking.'
    }
  ];

  const optionalSubjects = [
    'Agriculture', 'Animal Husbandry and Veterinary Science', 'Anthropology', 'Botany',
    'Chemistry', 'Civil Engineering', 'Commerce and Accountancy', 'Economics',
    'Electrical Engineering', 'Geography', 'Geology', 'History', 'Law',
    'Management', 'Mathematics', 'Mechanical Engineering', 'Medical Science',
    'Philosophy', 'Physics', 'Political Science and International Relations',
    'Psychology', 'Public Administration', 'Sociology', 'Statistics', 'Zoology',
    'Literature (22 Indian Languages available)'
  ];

  const features = [
    {
      icon: <FaBook />,
      title: 'Comprehensive Study Material',
      description: 'Updated course content covering entire UPSC syllabus with latest current affairs'
    },
    {
      icon: <FaUsers />,
      title: 'Expert Faculty',
      description: 'Learn from experienced educators and retired civil servants'
    },
    {
      icon: <FaFileAlt />,
      title: 'Regular Test Series',
      description: 'Prelims and Mains test series with detailed evaluation and feedback'
    },
    {
      icon: <FaLightbulb />,
      title: 'Personalized Mentorship',
      description: 'One-on-one guidance for strategy building and answer writing'
    },
    {
      icon: <FaClock />,
      title: 'Flexible Learning',
      description: 'Both online and offline classes with recorded lectures'
    },
    {
      icon: <FaCheckCircle />,
      title: 'Interview Preparation',
      description: 'Mock interviews and personality development sessions'
    }
  ];

  const toggleStream = (id) => {
    setExpandedStream(expandedStream === id ? null : id);
  };

  const toggleSubject = (id) => {
    setExpandedSubject(expandedSubject === id ? null : id);
  };

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      {/* Hero Section */}
      <div className="bg-success text-white py-5 my-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8 align-content-center ">
              <h1 className="display-4 fw-bold mb-3">UPSC Civil Services Examination Training</h1>
              <p className="lead mb-4">
                Comprehensive coaching for India's most prestigious examination. Join thousands of aspirants who have achieved their dream of serving the nation.
              </p>
              <button className="btn btn-light btn-lg px-4" > <a className='text-dark fw-bold' href="/enroll">Enroll Now </a></button>
            </div>
            <div className="col-lg-3 text-center">
              <FaGraduationCap size={150} className="opacity-75" />
            </div>
          </div>
        </div>
      </div>

      {/* Streams Section */}
      <div className="container py-5">
        <h2 className="text-center mb-5 fw-bold">Training for Various UPSC Streams</h2>
        <div className="row g-4">
          {streams.map((stream) => (
            <div key={stream.id} className="col-md-6">
              <div className="card h-100 shadow-sm border-0 hover-shadow" style={{ transition: 'all 0.3s' }}>
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="text-primary me-3" style={{ fontSize: '2.5rem' }}>
                      {stream.icon}
                    </div>
                    <h4 className="mb-0 fw-bold">{stream.title}</h4>
                  </div>
                  <p className="text-muted">{stream.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Exam Structure */}
      <div className="bg-white py-5">
        <div className="container">
          <h2 className="text-center mb-5 fw-bold">Examination Structure</h2>
          <div className="row g-4">
            {examStructure.map((exam, index) => (
              <div key={index} className="col-lg-4">
                <div className="card border-primary h-100">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0 fw-bold">Stage {index + 1}: {exam.stage}</h5>
                  </div>
                  <div className="card-body">
                    <p className="mb-2"><strong>Nature:</strong> {exam.nature}</p>
                    <p className="mb-3"><strong>Duration:</strong> {exam.duration}</p>
                    <h6 className="fw-bold mb-2">Papers:</h6>
                    <ul className="list-unstyled">
                      {exam.papers.map((paper, idx) => (
                        <li key={idx} className="mb-2">
                          <FaCheckCircle className="text-success me-2" />
                          {paper}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Subjects and Syllabus */}
      <div className="container py-5">
        <h2 className="text-center mb-5 fw-bold">Detailed Subject Coverage</h2>
        <div className="row">
          <div className="col-12">
            {subjects.map((subject) => (
              <div key={subject.id} className="card mb-3 border-0 shadow-sm">
                <div 
                  className="card-header bg-light cursor-pointer d-flex justify-content-between align-items-center"
                  onClick={() => toggleSubject(subject.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <h5 className="mb-1 fw-bold text-primary">{subject.name}</h5>
                    <small className="text-muted">{subject.category}</small>
                  </div>
                  {expandedSubject === subject.id ? <FaChevronUp /> : <FaChevronDown />}
                </div>
                {expandedSubject === subject.id && (
                  <div className="card-body">
                    <p className="mb-3"><strong>Overview:</strong> {subject.syllabus}</p>
                    <h6 className="fw-bold mb-3">Key Topics Covered:</h6>
                    <div className="row">
                      {subject.topics.map((topic, idx) => (
                        <div key={idx} className="col-md-6 mb-2">
                          <FaCheckCircle className="text-success me-2" />
                          <span>{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Optional Subjects */}
      <div className="bg-light py-5">
        <div className="container">
          <h2 className="text-center mb-4 fw-bold">Optional Subjects Available</h2>
          <p className="text-center text-muted mb-4">
            Choose one optional subject from the following list for Mains examination
          </p>
          <div className="row g-3">
            {optionalSubjects.map((subject, idx) => (
              <div key={idx} className="col-md-3 col-sm-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center p-3">
                    <FaBook className="text-primary mb-2" />
                    <p className="mb-0 small fw-semibold">{subject}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="container py-5">
        <h2 className="text-center mb-5 fw-bold">Why Choose Our Training Program?</h2>
        <div className="row g-4">
          {features.map((feature, idx) => (
            <div key={idx} className="col-md-4">
              <div className="card border-0 shadow-sm h-100 text-center p-4">
                <div className="text-primary mb-3" style={{ fontSize: '3rem' }}>
                  {feature.icon}
                </div>
                <h5 className="fw-bold mb-3">{feature.title}</h5>
                <p className="text-muted">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Information */}
      <div className=" bg-success text-white py-5">
        <div className="container">
          <h2 className="text-center text-white fw-bold fs-3 mb-5 fw-bold">Important Information</h2> <hr/>
          <div className="row g-4">
            <div className="col-md-3 text-center">
              <h3 className="fw-bold text-white">21-32</h3>
              <p className="mb-0">Age Limit (years)</p>
              <small>(Relaxation for reserved categories)</small>
            </div>
            <div className="col-md-3 text-center">
              <h3 className="fw-bold text-white">6</h3>
              <p className="mb-0">Maximum Attempts (General)</p>
              <small>(9 for OBC, Unlimited for SC/ST)</small>
            </div>
            <div className="col-md-3 text-center">
              <h3 className="fw-bold text-white">~1000</h3>
              <p className="mb-0">Vacancies Annually</p>
              <small>(Varies each year)</small>
            </div>
            <div className="col-md-3 text-center">
              <h3 className="fw-bold text-white ">3 Stages</h3>
              <p className="mb-0">Selection Process</p>
              <small>(Prelims, Mains, Interview)</small>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container py-5">
        <div className="card border-0 shadow-lg bg-gradient" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <div className="card-body text-dark text-center p-5">
            <h2 className="fw-bold mb-3">Ready to Start Your UPSC Journey?</h2>
            <p className="lead mb-4">
              Join our comprehensive training program and get personalized guidance from experts
            </p>
            <button className="btn btn-success btn-lg px-5 me-3">Get Started</button>
            <button className="btn btn-outline-dark btn-lg px-5">Download Brochure</button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default UPSCPage;